import React, { useState, useEffect } from "react";
import { dataService } from "../../../Services/data/data.service";
import { Box, } from '@mui/material';
import { makeStyles } from "@material-ui/core/styles";
import { dataListTypes, crudTypes, subscriptionTiers } from '../../../Config/constants';
import { Infinity, Plus, Search, Pencil, ReceiptEuro } from 'lucide-react';
import ResetFiltersButton from "../../Filter/ResetFiltersButton";
import { parseNewDateToDDMMYYYnoTime } from '../../../Utils/dateHelper';
import { SubscriberFilterTypes } from "../../../Config/constants";
import HeaderBar from '../../Shared/Header/HeaderBar';
import HeaderTitle from '../../Shared/Header/HeaderTitle';
import { useTranslation } from 'react-i18next';

// Components
import CreateSubscriberDialog from "./Components/CreateSubscriberDialog";
import SubscriberBillingDialog from "./Components/SubscriberBillingDialog";
import useFilterPersistence from '../../../hooks/useFilterPersistence';


function SubscriberOverviewPage(props) {
    const { t } = useTranslation();
    const [firstLoad, setFirstLoad] = useState(0);
    const classes = useStyles();
    const [filterOn, setFilterOn] = useState(false);

    const [selectedFilter, setSelectedFilter] = useState(1);

    // General
    const [generalError, setGeneralError] = useState(null);

    // Subscribers
    const [allSubscribers, setAllSubscribers] = useState([]);
    const [subscriberList, setSubscriberList] = useState([]);

    // CRUD
    const [createSubscriberDialogOpen, setCreateSubscriberDialogOpen] = useState(false);
    const [subscriberToEdit, setSubscriberToEdit] = useState(null);

    // Billing
    const [subscriberToBill, setSubscriberToBill] = useState(null);

    // ── Persistent filter state ────────────────────────────────────────────────
    const {
        filters,
        setFilterText,
        resetFilters,
        getFilterValue,
        hasActiveFilters,
    } = useFilterPersistence({
        pageKey: 'subscriber-overview',
        filterSchema: { name: SubscriberFilterTypes.NAME },
        defaultFilters: [],
    });

    useEffect(() => {
        dataService.getAllSubscribers().then(result => {
            setAllSubscribers(result);
        }).catch(error => {
            console.log(error);
            if (error.status === 403) {
                setGeneralError(t('errors.unauthorized'));
            }
        });
    }, [props.Login.userid])

    // Re-apply filters whenever the source data or active filters change (covers reset + URL restore)
    useEffect(() => {
        filterList(filters);
    }, [allSubscribers, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    if (firstLoad === 0) {
        setFirstLoad(1);
    }

    const filter = [
        { number: 1, column: "name", sortOrder: "ASC" },
        { number: 2, column: "name", sortOrder: "DESC" },
    ]

    const dynamicSorting = (property) => {
        var sortOrder = 1;
        if (property[0] === "-") {
            sortOrder = -1;
            property = property.substr(1);
        }
        let asc = false;
        /* SC: Filter now works both ways (ASC, DESC) */
        if (filter.find(x => x.number === selectedFilter).column === property) {
            if (filter.find(x => x.number === selectedFilter).sortOrder === 'ASC') {
                asc = false;
                setSelectedFilter(filter.find(x => x.column === property && x.sortOrder === 'DESC').number);
            } else {
                asc = true;
                setSelectedFilter(filter.find(x => x.column === property && x.sortOrder === 'ASC').number);
            }
        } else {
            asc = true;
            setSelectedFilter(filter.find(x => x.column === property && x.sortOrder === 'DESC').number);
        }
        if (asc === true) {
            return function (a, b) {
                //ASC A-Z
                var result = (a[property].toLowerCase() > b[property].toLowerCase() ? 1 : -1);
                return result * sortOrder;
            }
        } else {
            return function (a, b) {
                //DESC Z-A
                var result = (a[property].toLowerCase() < b[property].toLowerCase() ? 1 : -1);
                return result * sortOrder;
            }
        }
    }

    const handleSorting = (sortType) => {
        let tempSubscriberList = [...subscriberList]
        setSubscriberList(tempSubscriberList.sort(dynamicSorting(sortType)));
    }

    const toggleFilter = () => {
        if (filterOn) {
            resetFilters();
        }
        setFilterOn(!filterOn);
    }

    const updateFilters = (type, event) => {
        setFilterText(type, event.target.value);
        filterList([{ type, filterValue: event.target.value }, ...filters.filter(f => f.type !== type)]);
    }

    const filterList = (thisFilters, subscribers) => {
        let varFilteredlist = subscribers ? subscribers : [...allSubscribers];

        for (let i = 0; i < thisFilters.length; i++) {
            let filter = thisFilters[i]
            switch (filter.type) {
                case SubscriberFilterTypes.NAME:
                    varFilteredlist = varFilteredlist.filter(x => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
                    break;
                default:
                    varFilteredlist = varFilteredlist.filter(x => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
                    break;
            }
        }
        if (subscribers) {
            return varFilteredlist;
        } else {
            setSubscriberList(varFilteredlist);
        }
    }

    const openCreateSubscriberDialog = () => {
        setCreateSubscriberDialogOpen(true);
    }

    const closeCreateSubscriberDialog = () => {
        setCreateSubscriberDialogOpen(false);
        setSubscriberToEdit(null);
    }

    const handleSubscriberCrudSuccess = (subscriber, crudType) => {
        const subscriberId = subscriber.id;
        let tempAllSubscribers = [...allSubscribers];

        // Switch statement to handle different CRUD types
        switch (crudType) {
            case crudTypes.CREATE:
                tempAllSubscribers.push(subscriber);
                break;
            case crudTypes.UPDATE:
                tempAllSubscribers = tempAllSubscribers.map((s) => s.id === subscriberId ? subscriber : s);
                break;
            case crudTypes.DELETE:
                tempAllSubscribers = tempAllSubscribers.filter((s) => s.id !== subscriberId);
                break;
            default:
                console.error(`Unsupported CRUD operation: ${crudType}`);
                return;
        }

        setAllSubscribers(tempAllSubscribers);
        setSubscriberList(filterList(filters, tempAllSubscribers))
    };

    // Keep the row's administrator list and seat usage in sync after adding/removing an admin
    const handleAdminsChanged = (response) => {
        const updatedSubscriber = {
            ...subscriberToEdit,
            administrators: response.administrators.map((admin) => admin.emailAddress),
            userCount: response.userCount,
        };
        handleSubscriberCrudSuccess(updatedSubscriber, crudTypes.UPDATE);
    };

    const getSubscriptionBadge = (subscriptionTierId) => {
        // switch case
        switch (subscriptionTierId) {
            case subscriptionTiers.FLAGSTONE:
                return "Flagstone";
            case subscriptionTiers.TRYOUT:
                return "Tryout";
            case subscriptionTiers.PROFESSIONAL:
                return "Professional";
            case subscriptionTiers.ENTERPRISE:
                return "Enterprise";
            default:
                return "Unknown";
        }
    }

    const getAdminCell = (administrators) => {
        if (administrators && administrators.length > 0) {
            // return 1 string of all adminitrators seperated by a comma (ordered alphabetically)
            let adminString = "";
            let sortedAdmins = [...administrators].sort((a, b) => a.localeCompare(b));
            sortedAdmins.forEach((admin, index) => {
                adminString += admin;
                if (index !== sortedAdmins.length - 1) {
                    adminString += ", ";
                }
            });
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <p style={{ fontSize: "16px", color: "#0C0E29" }}>{adminString}</p>
                </div>
            )


        } else {
            return (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <p style={{ fontSize: "16px", color: "#0C0E29" }}>{t('subscriberOverview.noAdministrator')}</p>
                </div>
            )
        }
    }

    const getStatusIndicator = (isActive) => {
        let statusName = isActive ? t('common.active') : t('common.inactive');

        return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ height: "6px", width: "6px", borderRadius: "50%", backgroundColor: isActive ? "#5CC958" : "#D84949" }} />{statusName}
            </div>
        )
    }

    const getUsageIndicator = (count, limit) => {
        if (!limit) {
            return (
                <div style={{ display: "flex", alignItems: "center" }}>
                    {count}/<Infinity style={{ height: "20px", width: "20px", color: "#0C0E29", strokeWidth: "1.4px" }} />
                </div>
            )
        } else {
            return `${count}/${limit}`;
        }
    }

    const formatDate = (date) => {
        if (date) {
            let tempDate = new Date(date);
            return parseNewDateToDDMMYYYnoTime(tempDate)
        } else {
            return "";
        }
    }

    return (
        <React.Fragment>
            <Box className="page-container">
                <HeaderBar>
                    <HeaderTitle>{t('nav.subscribers')}</HeaderTitle>
                </HeaderBar>
                <div className={classes.tableManagementContainer}>
                    <div className={classes.tableManagementContainer__start}>
                        <div className={classes.searchContainer}>
                            <input placeholder={t('subscriberOverview.searchPlaceholder')} className={classes.search__input} type="text" value={getFilterValue(SubscriberFilterTypes.NAME)} onChange={(e) => updateFilters(SubscriberFilterTypes.NAME, e)} />
                            <Search size={16} className={classes.inputIcon} />
                        </div>
                        {/* Reset filters — visible whenever any filter is active */}
                        {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
                    </div>
                    <div className={classes.tableManagementContainer__end}>
                        <button className={classes.tableManagementContainer__primaryIconButton} onClick={() => openCreateSubscriberDialog()}>
                            <Plus className={classes.iconButton__icon} />
                            {t('subscriberOverview.addSubscriber')}
                        </button>
                    </div>
                </div>
                {generalError &&
                    <div className={classes.errorContainer}>
                        <p className={classes.errorMessage}>{generalError}</p>
                    </div>
                }
                <div className={classes.tableContainer}>
                    <table className={classes.table}>
                        <thead className={classes.table__header}>
                            <tr>
                                <th onClick={() => handleSorting("name")}>{t('common.name')}</th>
                                <th onClick={() => handleSorting("administrator")}>{t('subscriberOverview.administrator')}</th>
                                <th onClick={() => handleSorting("subscription")}>{t('subscriberOverview.subscription')}</th>
                                <th onClick={() => handleSorting("users")}>{t('subscriberOverview.users')}</th>
                                <th onClick={() => handleSorting("projects")}>{t('project.projects')}</th>
                                <th onClick={() => handleSorting("companies")}>{t('subscriberOverview.companies')}</th>
                                <th onClick={() => handleSorting("startDate")}>{t('results.startDate')}</th>
                                <th onClick={() => handleSorting("endDate")}>{t('results.endDate')}</th>
                                <th onClick={() => handleSorting("status")}>{t('common.status')}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody className={classes.table__body}>
                            {subscriberList.map((subscriber, index) => {
                                return (
                                    <tr className={classes.table__row} key={index}>
                                        <td>{subscriber.name}</td>
                                        <td>{getAdminCell(subscriber.administrators)}</td>
                                        <td>{getSubscriptionBadge(subscriber.subscriptionTierId)}</td>
                                        <td>{subscriber.subscriptionTierId && getUsageIndicator(subscriber.userCount, subscriber.userLimit)}</td>
                                        <td>{subscriber.subscriptionTierId && getUsageIndicator(subscriber.projectCount, subscriber.projectLimit)}</td>
                                        <td>{subscriber.subscriptionTierId && getUsageIndicator(subscriber.companyCount, subscriber.companyLimit)}</td>
                                        <td>{formatDate(subscriber.startDate)}</td>
                                        <td>{formatDate(subscriber.endDate)}</td>
                                        <td>{getStatusIndicator(subscriber.status)}</td>
                                        <td>
                                            <div className={classes.buttonContainer}>
                                                <button className={classes.iconButton} onClick={() => setSubscriberToBill(subscriber)}>
                                                    <ReceiptEuro className={classes.iconButton__icon} />
                                                </button>
                                                <button className={classes.iconButton} onClick={() => setSubscriberToEdit(subscriber)}>
                                                    <Pencil className={classes.iconButton__icon} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </Box>
            {/* <CreateAccountCategoryDialog dialogOpen={createAccountCategoryDialogOpen || accountCategoryToEdit} closeDialog={closeCreateAccountCategoryDialog} accountCategoryToEdit={accountCategoryToEdit} companies={accountCategoriesCompanies} userId={props.Login.userid} onSuccess={handleAccountCategoryCrudSuccess} />*/}
            <CreateSubscriberDialog dialogOpen={createSubscriberDialogOpen || subscriberToEdit} closeDialog={closeCreateSubscriberDialog} subscriberToEdit={subscriberToEdit} userId={props.Login.userid} onSuccess={handleSubscriberCrudSuccess} onAdminsChanged={handleAdminsChanged} />
            <SubscriberBillingDialog dialogOpen={subscriberToBill} closeDialog={() => setSubscriberToBill(null)} subscriberToBill={subscriberToBill} userId={props.Login.userid} />

        </React.Fragment >
    )
}

export default SubscriberOverviewPage;

const useStyles = makeStyles({
    tablesContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "32px",
    },

    header: {
        width: "100%",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "16px",
    },

    errorContainer: {
        width: "100%",
        backgroundColor: "#FDF3F3",

        border: "1px solid #FBE5E5",
        borderRadius: "6px",

        marginBottom: "16px",
        padding: "12px",
    },

    errorMessage: {
        fontSize: "16px",
        color: "#D84949"
    },

    tableManagementContainer: {
        width: "100%",
        height: "fit-content",

        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        flexWrap: "wrap-reverse",

        marginBottom: "8px",
    },

    tableManagementContainer__start: {
        width: "fit-content",

        display: "flex",
        alignItens: "center",
        gap: "8px",
    },

    searchContainer: {
        width: "240px",
        position: 'relative',
    },

    search__input: {
        width: '100%',
        height: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',

        padding: '0px 8px',
        paddingRight: '32px',

        borderRadius: "4px",
        border: "1px solid #DCDCDC",

        fontSize: '14px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",

        backgroundColor: "#FFFFFF",

        "&:focus": {
            outline: "none",
            border: "1px solid var(--color-primary)",
        },
    },

    inputIcon: {
        color: "#0C0E29",
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '8px',
    },

    tableManagementContainer__end: {
        width: "fit-content",

        display: "flex",
        alignItens: "center",
        gap: "8px",
    },

    tableManagementContainer__primaryIconButton: {
        height: "32px",
        padding: "0 12px",
        paddingLeft: "6px",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "white",
        backgroundColor: "var(--color-primary)",

        display: "flex",
        gap: "4px",
        alignItems: "center",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#a77d4f",
            color: "white",
            cursor: "pointer",
        }
    },


    buttonContainer: {
        display: "flex",
        gap: "8px",
        alignItems: "center",
    },

    iconButton: {
        height: "32px",
        width: "32px",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        borderRadius: "4px",
        border: "none",

        backgroundColor: "#F8F8F8",

        "&:hover": {
            backgroundColor: "#EDEDED",
        },
    },

    iconButton__icon: {
        height: "20px",
        width: "20px",

        display: "block",
    },

    tableContainer: {
        width: "100%",
        overflowX: "auto",
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        borderSpacing: "0",

        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
    },

    table__header: {
        backgroundColor: "#DFDFDF",
        borderRadius: "8px 8px 0px 0px",

        "& th": {
            height: "36px",
            padding: "0px 16px",
            textAlign: "left",
            fontSize: "16px",
            fontWeight: "normal",
            color: "#0C0E29",

            "&:first-child": {
                borderTopLeftRadius: "8px",
            },

            "&:last-child": {
                borderTopRightRadius: "8px",
                width: "0px",
                padding: "0px",
            },
        }
    },

    table__body: {
        "& tr": {
            borderBottom: "1px solid #EDEDED",

            "&:last-child": {
                borderBottom: "none",

                "& td": {
                    "&:first-child": {
                        borderBottomLeftRadius: "8px",
                    },

                    "&:last-child": {
                        borderBottomRightRadius: "8px",
                    },
                }
            },

            "& td": {
                height: "48px",
                padding: "0px 16px",
                textAlign: "left",
                fontSize: "16px",
                fontWeight: "normal",
                color: "#0C0E29",

                borderRight: "1px solid #EDEDED",

                "&:last-child": {
                    borderRight: "none",
                    width: "fit-content",
                    padding: "8px",
                },
            }
        },
    },

    table__row: {
        backgroundColor: "#FFFFFF !important",
    }


});